import { useState } from 'react';
import UtmColorPicker from './UtmColorPicker';
import UtmGeometryPreview from './UtmGeometryPreview';

interface StyleConfig {
  polygon?: {
    fillColor?: [number, number, number, number];
    outlineColor?: [number, number, number, number];
    outlineWidth?: number;
  };
  line?: {
    color?: [number, number, number, number];
    width?: number;
  };
  point?: {
    fillColor?: [number, number, number, number];
    radius?: number;
  };
}

interface UtmStyleEditorProps {
  style: StyleConfig;
  onChange: (style: StyleConfig) => void;
  previewGeometry?: any;
}

const UtmStyleEditor = ({ style, onChange, previewGeometry }: UtmStyleEditorProps) => {
  const [activeType, setActiveType] = useState<'polygon' | 'line' | 'point'>('polygon');
  const polygonOutlineWidth = style.polygon?.outlineWidth ?? style.line?.width ?? 2;

  const rgbaToHex = (rgba?: [number, number, number, number]): string => {
    if (!rgba) return '#3b82f6';
    const [r, g, b] = rgba;
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  };

  const hexToRgba = (hex: string, alpha: number): [number, number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16), alpha]
      : [0, 0, 0, alpha];
  };

  const handlePolygonFillColorChange = (hex: string) => {
    const alpha = style.polygon?.fillColor?.[3] ?? 100;
    onChange({
      ...style,
      polygon: {
        ...style.polygon,
        fillColor: hexToRgba(hex, alpha),
      },
    });
  };

  const handlePolygonFillOpacityChange = (opacity: number) => {
    const currentColor = style.polygon?.fillColor || [59, 130, 246, 100];
    onChange({
      ...style,
      polygon: {
        ...style.polygon,
        fillColor: [currentColor[0], currentColor[1], currentColor[2], opacity],
      },
    });
  };

  const handlePolygonOutlineColorChange = (hex: string) => {
    const alpha = style.polygon?.outlineColor?.[3] ?? 255;
    onChange({
      ...style,
      polygon: {
        ...style.polygon,
        outlineColor: hexToRgba(hex, alpha),
      },
    });
  };

  const handleLineColorChange = (hex: string) => {
    const alpha = style.line?.color?.[3] ?? 255;
    onChange({
      ...style,
      line: {
        ...style.line,
        color: hexToRgba(hex, alpha),
      },
    });
  };

  const handlePolygonOutlineWidthChange = (width: number) => {
    onChange({
      ...style,
      polygon: {
        ...style.polygon,
        outlineWidth: width,
      },
    });
  };

  const handlePointColorChange = (hex: string) => {
    const alpha = style.point?.fillColor?.[3] ?? 255;
    onChange({
      ...style,
      point: {
        ...style.point,
        fillColor: hexToRgba(hex, alpha),
      },
    });
  };

  const handlePointRadiusChange = (radius: number) => {
    onChange({
      ...style,
      point: {
        ...style.point,
        radius,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Type Selector */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveType('polygon')}
          className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
            activeType === 'polygon'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
        >
          Polygon
        </button>
        <button
          type="button"
          onClick={() => setActiveType('line')}
          className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
            activeType === 'line'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
        >
          Line
        </button>
        <button
          type="button"
          onClick={() => setActiveType('point')}
          className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
            activeType === 'point'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
        >
          Point
        </button>
      </div>

      {/* Polygon Styles */}
      {activeType === 'polygon' && (
        <div className="space-y-4">
          <UtmColorPicker
            label="Fill Color"
            color={rgbaToHex(style.polygon?.fillColor)}
            onChange={handlePolygonFillColorChange}
          />

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Fill Opacity</label>
            <input
              type="range"
              min="0"
              max="255"
              value={style.polygon?.fillColor?.[3] ?? 100}
              onChange={(e) => handlePolygonFillOpacityChange(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-xs text-muted-foreground">
              {Math.round(((style.polygon?.fillColor?.[3] ?? 100) / 255) * 100)}%
            </div>
          </div>

          <UtmColorPicker
            label="Outline Color"
            color={rgbaToHex(style.polygon?.outlineColor)}
            onChange={handlePolygonOutlineColorChange}
          />

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Outline Width</label>
            <input
              type="number"
              min="1"
              max="10"
              value={polygonOutlineWidth}
              onChange={(e) => handlePolygonOutlineWidthChange(Number(e.target.value))}
              className="w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground"
            />
          </div>
        </div>
      )}

      {/* Line Styles */}
      {activeType === 'line' && (
        <div className="space-y-4">
          <UtmColorPicker
            label="Line Color"
            color={rgbaToHex(style.line?.color)}
            onChange={handleLineColorChange}
          />

        </div>
      )}

      {/* Point Styles */}
      {activeType === 'point' && (
        <div className="space-y-4">
          <UtmColorPicker
            label="Point Color"
            color={rgbaToHex(style.point?.fillColor)}
            onChange={handlePointColorChange}
          />

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Point Radius</label>
            <input
              type="number"
              min="1"
              max="20"
              value={style.point?.radius ?? 5}
              onChange={(e) => handlePointRadiusChange(Number(e.target.value))}
              className="w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground"
            />
          </div>
        </div>
      )}

      {/* Preview */}
      {previewGeometry && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Style Preview</label>
          <UtmGeometryPreview
            geometry={previewGeometry}
            style={{
              fillColor: rgbaToHex(style.polygon?.fillColor),
              outlineColor: rgbaToHex(style.polygon?.outlineColor),
              fillOpacity: (style.polygon?.fillColor?.[3] ?? 100) / 255,
              outlineWidth: polygonOutlineWidth,
            }}
            height="200px"
          />
        </div>
      )}
    </div>
  );
};

export default UtmStyleEditor;
